import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const ComplianceReportsPanel = () => {
  const [reportType, setReportType] = useState('gst');
  const [startDate, setStartDate] = useState('2025-11-01');
  const [endDate, setEndDate] = useState('2025-11-30');

  const complianceReports = [
    {
      id: "RPT2025001",
      type: "GST Returns",
      period: "November 2025",
      generatedDate: "2025-12-01",
      status: "completed",
      totalTransactions: 1216,
      totalTaxCollected: 2189760,
      fileSize: "2.4 MB"
    },
    {
      id: "RPT2025002",
      type: "E-Way Bills",
      period: "November 2025",
      generatedDate: "2025-12-01",
      status: "completed",
      totalBills: 1089,
      totalValue: 32670000,
      fileSize: "1.8 MB"
    },
    {
      id: "RPT2025003",
      type: "TDS Compliance",
      period: "Q3 2025",
      generatedDate: "2025-11-30",
      status: "completed",
      totalDeductions: 456000,
      transactionCount: 234,
      fileSize: "890 KB"
    },
    {
      id: "RPT2025004",
      type: "Insurance Audit",
      period: "November 2025",
      generatedDate: "2025-11-29",
      status: "completed",
      totalPolicies: 1089,
      totalPremium: 163350,
      fileSize: "1.2 MB"
    },
    {
      id: "RPT2025005",
      type: "Payment Reconciliation",
      period: "November 2025",
      generatedDate: "2025-11-28",
      status: "processing",
      totalPayments: 1216,
      totalAmount: 36480000,
      fileSize: "3.1 MB"
    }
  ];

  const auditLogs = [
    {
      id: "AUD2025001",
      action: "GST Report Generated",
      user: "Admin User",
      timestamp: "2025-12-01 09:30:00",
      details: "Generated GST returns report for November 2025",
      ipAddress: "192.168.1.100"
    },
    {
      id: "AUD2025002",
      action: "User KYC Approved",
      user: "Admin User",
      timestamp: "2025-11-30 16:45:00",
      details: "Approved KYC verification for user USR2025001",
      ipAddress: "192.168.1.100"
    },
    {
      id: "AUD2025003",
      action: "Payment Dispute Resolved",
      user: "Admin Team A",
      timestamp: "2025-11-30 14:20:00",
      details: "Resolved payment dispute DSP2025002 with partial refund",
      ipAddress: "192.168.1.101"
    },
    {
      id: "AUD2025004",
      action: "Document Access",
      user: "Admin User",
      timestamp: "2025-11-29 11:15:00",
      details: "Accessed invoice document for booking SLB2025001",
      ipAddress: "192.168.1.100"
    }
  ];

  const reportTypeOptions = [
    { value: 'gst', label: 'GST Returns' },
    { value: 'eway', label: 'E-Way Bills' },
    { value: 'tds', label: 'TDS Compliance' },
    { value: 'insurance', label: 'Insurance Audit' },
    { value: 'payment', label: 'Payment Reconciliation' }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'processing': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getReportIcon = (type) => {
    switch(type) {
      case 'GST Returns': return 'FileText';
      case 'E-Way Bills': return 'Truck';
      case 'TDS Compliance': return 'Receipt';
      case 'Insurance Audit': return 'Shield';
      case 'Payment Reconciliation': return 'DollarSign';
      default: return 'File';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const handleGenerateReport = () => {
    console.log('Generating report:', { reportType, startDate, endDate });
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Compliance Reports</h2>
              <p className="text-sm text-muted-foreground">Generate and manage regulatory reports</p>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Generate New Report</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select
              label="Report Type"
              options={reportTypeOptions}
              value={reportType}
              onChange={setReportType}
              placeholder="Select report type"
            />
            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e?.target?.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e?.target?.value)}
            />
            <div className="flex items-end">
              <Button 
                variant="default" 
                fullWidth 
                iconName="FileText" 
                iconPosition="left"
                onClick={handleGenerateReport}
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {complianceReports?.map((report) => (
            <div key={report?.id} className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={getReportIcon(report?.type)} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{report?.type}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report?.status)}`}>
                        {report?.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Icon name="Hash" size={14} />
                        {report?.id}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        Period: {report?.period}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        Generated: {new Date(report.generatedDate)?.toLocaleDateString('en-IN')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="HardDrive" size={14} />
                        Size: {report?.fileSize}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 mt-2">
                      {report?.totalTransactions && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total Transactions</p>
                          <p className="text-sm font-semibold text-foreground">{report?.totalTransactions}</p>
                        </div>
                      )}
                      {report?.totalTaxCollected && (
                        <div>
                          <p className="text-xs text-muted-foreground">Tax Collected</p>
                          <p className="text-sm font-semibold text-primary">{formatCurrency(report?.totalTaxCollected)}</p>
                        </div>
                      )}
                      {report?.totalBills && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total Bills</p>
                          <p className="text-sm font-semibold text-foreground">{report?.totalBills}</p>
                        </div>
                      )}
                      {report?.totalValue && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total Value</p>
                          <p className="text-sm font-semibold text-primary">{formatCurrency(report?.totalValue)}</p>
                        </div>
                      )}
                      {report?.totalDeductions && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total Deductions</p>
                          <p className="text-sm font-semibold text-primary">{formatCurrency(report?.totalDeductions)}</p>
                        </div>
                      )}
                      {report?.totalPolicies && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total Policies</p>
                          <p className="text-sm font-semibold text-foreground">{report?.totalPolicies}</p>
                        </div>
                      )}
                      {report?.totalAmount && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total Amount</p>
                          <p className="text-sm font-semibold text-primary">{formatCurrency(report?.totalAmount)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="Eye">
                    View
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download">
                    Download
                  </Button>
                  <Button variant="outline" size="sm" iconName="Share2">
                    Share
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Audit Trail</h2>
              <p className="text-sm text-muted-foreground">System activity and access logs</p>
            </div>
          </div>
          <Button variant="outline" iconName="Download" iconPosition="left">
            Export Logs
          </Button>
        </div>

        <div className="space-y-3">
          {auditLogs?.map((log) => (
            <div key={log?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{log?.action}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{log?.details}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={12} />
                        {log?.user}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {log?.timestamp}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Globe" size={12} />
                        {log?.ipAddress}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceReportsPanel;